/**
 * Excel download/upload for `data_table` fields (e.g. the hyreslista).
 *
 * Injected into the field renderer via `FieldRendererProps.dataTableXlsx`
 * so the templates package stays free of the xlsx libraries. Both libs are
 * dynamic-imported here, so they're only fetched when the user actually
 * downloads a template or uploads a file (never in the main bundle).
 *
 * Phase 2 (mode 2): download OUR template (header row of the canonical
 * column labels) → fill in → upload → direct extraction by matching the
 * uploaded headers back to our column keys. No mapping/Bedrock needed —
 * that's Phase 3 for arbitrary/custom spreadsheets.
 */

import type {
  DataTableColumn,
  DataTableRow,
  DataTableXlsxIO,
} from '@hyranai/regta-templates';

const norm = (s: string): string => s.toLowerCase().replace(/\s+/g, ' ').trim();

function toCellNumber(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const n = Number.parseFloat(
    String(raw).replace(/\s/g, '').replace(',', '.').replace(/[^0-9.-]/g, ''),
  );
  return Number.isFinite(n) ? n : null;
}

export const dataTableXlsxIO: DataTableXlsxIO = {
  async downloadTemplate(columns: ReadonlyArray<DataTableColumn>, filename: string) {
    const writeXlsxFile = (await import('write-excel-file/browser')).default;
    const headerRow = columns.map((c) => ({
      value: c.label,
      type: String,
      fontWeight: 'bold' as const,
    }));
    await writeXlsxFile([headerRow] as never, {
      fileName: filename,
      columns: columns.map(() => ({ width: 22 })),
    } as never);
  },

  async parseUpload(
    file: File,
    columns: ReadonlyArray<DataTableColumn>,
  ): Promise<DataTableRow[]> {
    const readXlsxFile = (await import('read-excel-file/browser')).default;
    const rows = (await readXlsxFile(file)) as unknown as Array<
      Array<string | number | boolean | Date | null>
    >;
    if (rows.length < 2) return [];

    // Map each uploaded header column → our canonical column (by label).
    const header = (rows[0] ?? []).map((h) => norm(String(h ?? '')));
    const idxToCol = new Map<number, DataTableColumn>();
    header.forEach((h, i) => {
      const col = columns.find((c) => norm(c.label) === h);
      if (col) idxToCol.set(i, col);
    });
    if (idxToCol.size === 0) {
      throw new Error(
        'Hittade inga kolumner som matchar mallen. Använd "Ladda ner mall" och fyll i den.',
      );
    }

    const out: DataTableRow[] = [];
    for (let r = 1; r < rows.length; r++) {
      const row = rows[r] ?? [];
      if (row.every((c) => c == null || String(c).trim() === '')) continue;
      const obj: DataTableRow = {};
      idxToCol.forEach((col, i) => {
        const raw = row[i];
        if (raw == null || raw === '') {
          obj[col.key] = '';
        } else if (col.kind === 'currency' || col.kind === 'number') {
          const n = toCellNumber(raw);
          obj[col.key] = n == null ? String(raw) : n;
        } else if (raw instanceof Date) {
          obj[col.key] = raw.toISOString().slice(0, 10);
        } else {
          obj[col.key] = String(raw);
        }
      });
      out.push(obj);
    }
    return out;
  },
};
